/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- diff operates on `any` (alias `a`) by design */
export function diff<T extends Record<string, a>>(
  old: T | undefined,
  new_: T | undefined,
): Partial<T> {
  if (old === new_) return {}

  const isNil = (v: a) => v === undefined || v === null

  if (isNil(old) && !isNil(new_)) return new_ as Partial<T>
  if (isNil(new_) && !isNil(old)) return old as Partial<T>

  if (isNil(old) || isNil(new_)) return {} // eg old null, new undefined

  const patch: a = {}

  const keys = new Set([
    ...Object.keys(old as object),
    ...Object.keys(new_ as object),
  ])

  for (const key of keys) {
    const oldV = (old as Record<string, a>)[key]
    const newV = (new_ as Record<string, a>)[key]

    if (oldV === newV) continue

    if (oldV instanceof Date || newV instanceof Date) {
      const oldTime = oldV instanceof Date ? oldV.getTime() : undefined // one of the values can be not a date and not nullish
      const newTime = newV instanceof Date ? newV.getTime() : undefined
      if (oldTime !== newTime) patch[key] = newV
      continue
    }

    if (typeof oldV !== 'object' && typeof newV !== 'object') {
      if (oldV !== newV) patch[key] = newV
      continue
    }

    // Explicitly handle null transitions
    if (oldV === null && newV !== null) {
      patch[key] = newV
      continue
    }

    if (oldV !== null && newV === null) {
      patch[key] = null
      continue
    }

    if (oldV === undefined && newV !== undefined) {
      patch[key] = newV
      continue
    }

    if (oldV !== undefined && newV === undefined) {
      patch[key] = undefined
      continue
    }

    if (Array.isArray(oldV) && Array.isArray(newV)) {
      const isObjectWithId = (item: a) =>
        item !== null &&
        typeof item === 'object' &&
        'id' in item &&
        item.id !== undefined &&
        item.id !== null

      const bothHaveIds =
        oldV.every((v) => isObjectWithId(v)) &&
        newV.every((v) => isObjectWithId(v))

      if (!bothHaveIds) {
        // Fallback: treat arrays without ids (or with null items) as full replacement
        // Only set patch if arrays differ (shallow equality by index)
        const sameLength = oldV.length === newV.length
        const sameItems = sameLength && oldV.every((v, i) => v === newV[i])
        if (!sameItems) patch[key] = newV
        continue
      }

      const added: a[] = []
      const deleted: { id: string; _i_: -1 }[] = []
      const updated: a[] = []

      const oldMap = new Map(oldV.map((item) => [item.id, item]))
      const newMap = new Map(newV.map((item) => [item.id, item]))

      for (const oldItem of oldV) {
        if (!newMap.has(oldItem.id)) {
          deleted.push({ id: oldItem.id, _i_: -1 })
        }
      }

      for (const newItem of newV) {
        const oldItem = oldMap.get(newItem.id)

        if (oldItem) {
          const difference = diff(oldItem, newItem)
          if (Object.keys(difference).length > 0) {
            const updatedItem = { id: newItem.id, ...difference }
            updated.push(updatedItem)
          }
        } else {
          added.push(newItem)
        }
      }

      if (added.length > 0 || deleted.length > 0 || updated.length > 0) {
        patch[key] = [...deleted, ...added, ...updated]
      }

      continue
    }

    if (
      typeof oldV === 'object' &&
      oldV !== null &&
      typeof newV === 'object' &&
      newV !== null
    ) {
      const nestedDiff = diff(oldV, newV)
      if (Object.keys(nestedDiff).length > 0) {
        patch[key] = nestedDiff
      }
      continue
    }

    throw new Error(`diff: possible type mismatch for key ${key}`)
  }

  return patch
}

// oxlint-disable-next-line typescript/no-explicit-any -- generic diff utility needs any type
type a = any

// String diffing

type StringOp = { op: 'ins' | 'del' | 'no'; text: string; repl?: boolean }
type StringOps = StringOp[]

const splitIntoWords = (str: string) =>
  str.match(/\b\w+\b|[^\w\s]+|\s+/gu) ?? []

export const diffStrings = (received: string, target: string): StringOps =>
  diffWordArrays(splitIntoWords(received), splitIntoWords(target))

function computeDistances(words1: string[], words2: string[]): number[][] {
  const len1 = words1.length
  const len2 = words2.length
  const dp: number[][] = Array.from({ length: len1 + 1 }, () =>
    Array.from<number>({ length: len2 + 1 }).fill(0),
  )

  for (let i = 0; i <= len1; i++) dp[i][0] = i
  for (let j = 0; j <= len2; j++) dp[0][j] = j

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (words1[i - 1] === words2[j - 1]) dp[i][j] = dp[i - 1][j - 1]
      else dp[i][j] = 1 + Math.min(dp[i][j - 1], dp[i - 1][j], dp[i - 1][j - 1])
    }
  }
  return dp
}

function diffWordArrays(words1: string[], words2: string[]): StringOps {
  const len1 = words1.length
  const len2 = words2.length
  const dp = computeDistances(words1, words2)

  const ops: (
    | { op: 'no' | 'ins' | 'del'; char: string }
    | { op: 'rep'; char: string; from: string }
  )[] = []

  let i = len1
  let j = len2

  while (i > 0 || j > 0) {
    if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      ops.push({ op: 'del', char: words1[i - 1] })
      i--
    } else if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
      ops.push({ op: 'ins', char: words2[j - 1] })
      j--
    } else {
      if (words1[i - 1] === words2[j - 1]) {
        ops.push({ op: 'no', char: words1[i - 1] })
      } else {
        ops.push({ op: 'rep', char: words2[j - 1], from: words1[i - 1] })
      }
      i--
      j--
    }
  }

  ops.reverse()

  // Convert replacements to del+ins pairs
  const expandedOps: StringOps = []
  for (const op of ops) {
    if (op.op === 'rep') {
      expandedOps.push(
        { op: 'del', text: op.from, repl: true },
        { op: 'ins', text: op.char, repl: true },
      )
    } else {
      expandedOps.push({ op: op.op, text: op.char })
    }
  }

  // Merge consecutive ops of same type
  const mergedOps: StringOp[] = []
  let currentOp: StringOp | undefined

  for (const op of expandedOps) {
    if (currentOp && currentOp.op === op.op) {
      currentOp.text += op.text
    } else {
      if (currentOp) mergedOps.push(currentOp)
      currentOp = { op: op.op, text: op.text }
    }
  }

  if (currentOp) mergedOps.push(currentOp)

  // Optimize del+ins pairs where ins starts with del content (minus punctuation)
  for (let idx = 0; idx < mergedOps.length; idx++) {
    const op = mergedOps[idx]
    const nextOp = mergedOps[idx + 1]
    if (op.op === 'del' && nextOp?.op === 'ins') {
      const noPunctuation = op.text.replaceAll(/[.,!?;:]/gu, '')
      if (nextOp.text.startsWith(noPunctuation)) {
        nextOp.text = nextOp.text.slice(noPunctuation.length).trimStart()
        op.text = noPunctuation
        op.op = 'no'
      }
    }
  }

  return mergedOps
}
